import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Link } from "react-router-dom";
import BASE_URL from "../../configs/api";
import { getProxyUrl } from "../../utils/proxy";

const MovieInfoBlock = () => {
  const { slug } = useParams();
  const [movie, setMovie] = useState(null);
  const [error, setError] = useState(null);
  const [showFullSynopsis, setShowFullSynopsis] = useState(false);

  useEffect(() => {
    const fetchMovie = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/phim/${slug}`);
        setMovie(response.data);
      } catch (err) {
        setError("Failed to fetch movie data");
      } finally {
      }
    };
    if (slug) {
      fetchMovie();
    }
  }, [slug]);

  if (!movie) {
    return null;
  }

  return (
    <div className="">
      <div className="flex flex-col md:flex-row gap-6 lg:gap-8">
        {/* Left Section - Poster & Main Info */}
        <div className="flex flex-row w-full md:w-[60%] gap-0 md:gap-6">
          {/* Poster */}
          <div className="flex-shrink-0">
            <div className="hidden md:block relative w-24 h-36 md:w-32 md:h-48 lg:w-40 lg:h-60 rounded-lg overflow-hidden shadow-xl border border-gray-600/50 bg-gray-800">
              <img
                src={movie.poster_url || getProxyUrl(movie.poster)}
                alt={`Poster ${movie.ten_phim || "phim"}`}
                className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            </div>
          </div>

          {/* Movie Details */}
          <div className="flex-1 min-w-0">
            {/* Title */}
            <h1 className="text-xl md:text-xl lg:text-2xl font-bold text-white mb-2 leading-tight">
              <span className="md:hidden text-blue-400">Xem Phim </span>
              {movie.ten_phim || "No Title"}
            </h1>

            {movie.ten_khac && (
              <p className="text-sm text-gray-400 mb-3 italic">
                {movie.ten_khac}
              </p>
            )}

            {/* Quality & Info Tags */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="px-2 py-0.5 rounded-md text-xs font-bold text-white tracking-wide shadow-lg border border-orange-500 bg-transparent">
                {movie.chat_luong}
              </span>
              <span className="px-2 py-0.5 rounded-md text-xs font-semibold shadow-md hover:shadow-lg transition-shadow text-black bg-white border border-white">
                {movie.tinh_trang}
              </span>

              <span className="px-2 py-0.5 rounded-md text-xs font-semibold shadow-md hover:shadow-lg transition-shadow text-white border border-white bg-transparent">
                {movie.nam_phat_hanh}
              </span>
              <span className="px-2 py-0.5 rounded-md text-xs font-semibold shadow-md hover:shadow-lg transition-shadow text-white border border-white bg-transparent">
                {movie.thoi_luong}
              </span>
            </div>

            {/* Genres */}
            <div className="flex flex-wrap gap-2 mb-0 sm:mb-4 md:mb-4">
              {movie.the_loai && movie.the_loai.length > 0 ? (
                movie.the_loai.slice(0, 4).map((genre) => (
                  <Link
                    key={genre.slug}
                    className="hidden sm:block px-2 py-1 bg-orange-200/40 hover:bg-orange-300/60  text-white text-xs font-medium rounded-lg backdrop-blur-sm border border-white/20 transition-all duration-500 hover:scale-105"
                  >
                    {genre.ten}
                  </Link>
                ))
              ) : (
                <span className="hidden sm:block text-gray-400 italic text-sm">
                  Đang cập nhật
                </span>
              )}
            </div>

            {/* Status */}
            {movie.tinh_trang && (
              <div className="hidden sm:block mb-4">
                {(() => {
                  const tinhTrang = movie.tinh_trang.toLowerCase().trim();
                  const isCompleted =
                    tinhTrang.includes("hoàn tất") ||
                    tinhTrang.includes("full");
                  return (
                    <div
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
                        isCompleted
                          ? "bg-green-900/30 text-green-400 border border-green-700/50"
                          : "bg-orange-900/30 text-orange-400 border border-orange-700/50"
                      }`}
                    >
                      {isCompleted ? (
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2.5}
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      ) : (
                        <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                      )}
                      <span>Tình trạng: {movie.tinh_trang}</span>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>

        {/* Right Section - Description */}
        <div className="hidden sm:flex flex-1 min-w-0">
          <div className="space-y-4">
            <div className="flex gap-2 items-center">
              <h3 className="text-lg font-semibold text-white border-b border-gray-600">
                Nội dung phim
              </h3>
              <div className="">
                <Link
                  to={`/phim/${slug}`}
                  className="inline-flex items-center gap-2"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </Link>
              </div>
            </div>

            <div
              className={`text-sm text-gray-300 leading-relaxed ${
                !showFullSynopsis ? "line-clamp-4" : ""
              }`}
              dangerouslySetInnerHTML={{
                __html: movie.mo_ta || "Đang cập nhật nội dung phim...",
              }}
            />

            {movie.mo_ta && movie.mo_ta.length > 300 && (
              <button
                onClick={() => setShowFullSynopsis(!showFullSynopsis)}
                className="text-orange-400 hover:text-orange-300 text-sm font-medium transition-colors"
              >
                {showFullSynopsis ? "Thu gọn" : "Xem thêm"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MovieInfoBlock;
